/**
 * Exportación a Excel de las pestañas de "Creditos y Movimientos", y el aplanado de registros que
 * comparte con el PDF.
 *
 * `construirTabla` corre una sola vez y los dos documentos consumen su salida, para que el Excel y
 * el PDF no puedan decir cosas distintas del mismo listado. Las columnas vienen de
 * `definicion.columnasExport(ctx)` (ver definicionesTablas.js).
 *
 * Los estilos y los constructores de celda salen de ReportesNomina/estilosExcelNomina.js: ahí ya
 * está resuelto el formato Contabilidad y el `t: "n"` explícito que xlsx-js-style necesita para no
 * escribir los números como texto.
 */

import * as XLSXStyle from 'xlsx-js-style'
import dayjs from 'dayjs'
import {
  headerStyle, textoStyle, centroStyle, numeroStyle, monedaStyle,
  totalTextoStyle, totalNumeroStyle, totalMonedaStyle,
  celda, celdaNumero, celdaMoneda,
} from '../ReportesNomina/estilosExcelNomina'
import { fmtFecha } from './definicionesTablas'

// ── Aplanado ─────────────────────────────────────────────────────────────────

/** El tipo puede depender del renglón (el Importe de un movimiento es pesos o días según el caso). */
const tipoDeCelda = (columna, registro) =>
  (typeof columna.tipo === 'function' ? columna.tipo(registro) : columna.tipo) || 'texto'

/** Las de dinero se totalizan siempre; una numérica sólo si declaró `suma`. */
const esSumable = (tipo, columna) => tipo === 'moneda' || (tipo === 'numero' && columna.suma)

/** Una fecha sin valor sale vacía y no como "—": en una hoja de cálculo el guión estorba. */
export const textoFecha = (valor) => (valor ? fmtFecha(valor) : '')

/**
 * Los helpers de la tabla devuelven "—" cuando no hay dato, que en pantalla se lee bien pero en un
 * archivo es basura: rompe el ordenamiento y los filtros de Excel. En el export vale más el vacío.
 */
const limpiarGuion = (valor) => (valor === '—' ? '' : valor)

/**
 * Convierte los registros del servidor en la matriz que consumen el Excel y el PDF.
 *
 * @returns {{columnas: object[], filas: {valor:*, tipo:string}[][], totales: (object|null)[]}}
 *          `totales` es paralelo a `columnas`: `null` donde no se suma, `{valor, tipo}` donde sí.
 */
export function construirTabla({ definicion, ctx, registros }) {
  const columnas = definicion.columnasExport(ctx || {})
  const lista = registros || []

  // Una columna sumable arranca en cero y siempre muestra total, aunque todos sus renglones vengan
  // vacíos: si dependiera de encontrar un valor, `null` totalizaría (Number(null) es 0) y
  // `undefined` no (es NaN), y la misma columna llevaría total unos días sí y otros no.
  // Las de tipo mixto son la excepción: sólo totalizan si de verdad hubo un renglón de dinero.
  const acumulados = columnas.map((columna) => {
    if (typeof columna.tipo === 'function') return null
    return esSumable(columna.tipo, columna) ? { valor: 0, tipo: columna.tipo } : null
  })

  const filas = lista.map((registro) =>
    columnas.map((columna, i) => {
      const tipo = tipoDeCelda(columna, registro)
      const valor = limpiarGuion(columna.valor(registro))

      if (esSumable(tipo, columna)) {
        const numero = Number(valor)
        if (Number.isFinite(numero)) {
          if (!acumulados[i]) acumulados[i] = { valor: 0, tipo }
          acumulados[i].valor += numero
        }
      }

      return { valor, tipo }
    })
  )

  // Sin renglones no hay nada que totalizar: una fila de ceros sola confundiría.
  return { columnas, filas, totales: lista.length ? acumulados : columnas.map(() => null) }
}

// ── Excel ────────────────────────────────────────────────────────────────────

const tituloDocStyle = { font: { bold: true, sz: 14 } }
const subtituloDocStyle = { font: { sz: 11 } }
const metaDocStyle = { font: { sz: 9, italic: true, color: { rgb: '808080' } } }

const ESTILO_POR_TIPO = {
  texto: textoStyle,
  centro: centroStyle,
  numero: numeroStyle,
  moneda: monedaStyle,
  fecha: centroStyle,
}

const celdaDeDato = ({ valor, tipo }) => {
  if (tipo === 'moneda') return celdaMoneda(valor, monedaStyle)
  if (tipo === 'numero') return celdaNumero(valor, numeroStyle)
  if (tipo === 'fecha') return celda(textoFecha(valor), centroStyle)
  return celda(valor, ESTILO_POR_TIPO[tipo] || textoStyle)
}

// NFD parte cada letra acentuada en letra + diacrítico combinante (U+0300–U+036F); quitando el
// rango se van los acentos sin tener que enumerar á/é/í/ó/ú/ñ a mano.
const DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g')

/** Sin acentos ni caracteres que Windows no acepta en un nombre de archivo. */
const limpiarNombre = (texto) =>
  (texto || 'Listado')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')

export function nombreArchivo(definicion) {
  return `${limpiarNombre(definicion.titulo)}_${dayjs().format('YYYY-MM-DD')}`
}

/**
 * Arma y descarga la hoja del módulo.
 *
 * Sin `!freeze` ni `!autofilter`: el primero no hace nada (es de SheetJS Pro) y el segundo se traga
 * la fila de totales.
 */
export function descargarExcel({ definicion, ctx, registros, empresaNombre, descripcionFiltros }) {
  const { columnas, filas, totales } = construirTabla({ definicion, ctx, registros })

  const aoa = []
  aoa.push([celda(empresaNombre || '', tituloDocStyle)])
  aoa.push([celda(definicion.titulo, subtituloDocStyle)])
  aoa.push([celda(descripcionFiltros || '', metaDocStyle)])
  aoa.push([celda(
    `Generado ${dayjs().format('DD/MM/YYYY HH:mm')} · ${filas.length} registro(s)`,
    metaDocStyle
  )])
  aoa.push([])

  aoa.push(columnas.map((c) => celda(c.titulo, headerStyle)))
  filas.forEach((fila) => aoa.push(fila.map(celdaDeDato)))

  if (totales.some(Boolean)) {
    aoa.push(columnas.map((columna, i) => {
      if (i === 0) return celda('TOTAL', totalTextoStyle)
      const total = totales[i]
      if (!total) return celda('', totalTextoStyle)
      return total.tipo === 'moneda'
        ? celdaMoneda(total.valor, totalMonedaStyle)
        : celdaNumero(total.valor, totalNumeroStyle)
    }))
  }

  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa)
  hoja['!cols'] = columnas.map((c) => ({ wch: c.ancho || 14 }))

  const libro = XLSXStyle.utils.book_new()
  // El nombre de hoja de Excel topa en 31 caracteres y no acepta : \ / ? * [ ]
  XLSXStyle.utils.book_append_sheet(libro, hoja, definicion.titulo.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31))
  XLSXStyle.writeFile(libro, `${nombreArchivo(definicion)}.xlsx`)
}
